<template>
  <div class="on-hold-orders-notifications">
    <expand-transition>
      <sb-panel
        v-if="hasOnHoldOrderNotifications"
        variation="danger"
        icon-orientation="start"
        class="on-hold-orders-notifications__panel"
        :class="resolvePaddingClass"
      >
        <div>
          <div class="on-hold-orders-notifications__wrapper on-hold-orders-notifications__text--bold">
            <p class="on-hold-orders-notifications__text" v-html="$t('onHoldNotifications.noticeA', { totalOrdersAffected })"/>
            <router-link
              class="on-hold-orders-notifications__link"
              target='_blank'
              :to="{ name: OrdersEnum.OrdersWithView, params: { view: 'On Hold' } }"
            >
              on hold
            </router-link>
            <p class="on-hold-orders-notifications__text">{{ $t('onHoldNotifications.noticeB') }}</p>
            <help-center-link linkKey="orders/status/onHold" class="on-hold-orders-notifications__link">{{ $t('onHoldNotifications.noticeHelp') }}</help-center-link>
          </div>
          <sb-anchor
            class="on-hold-orders-notifications__link"
            @click.prevent="handleShowNotificationDetails"
            href="#"
          >
            {{ $t('onHoldNotifications.expandText') }}
            <span
              class="icon icon-chevron-left on-hold-orders-notifications__expand-icon"
              :class="{ 'on-hold-orders-notifications__expand-icon--expanded': showNotificationDetails }"
            />
          </sb-anchor>
          <expand-transition>
            <div class="on-hold-orders-notifications__notifications-container" v-if="showNotificationDetails">
              <div v-for="notification in prioritizedNotifications" :key="notification.id">
                <p class="on-hold-orders-notifications__text" v-html="resolveNotificationName(notification)" />
                <ul class="on-hold-orders-notifications__list">
                  <li class="on-hold-orders-notifications__list-item" v-for="detail in notification.details" :key="detail.reference + detail.id">
                    <p
                      v-html="resolveReasonText(detail)"
                      class="on-hold-orders-notifications__text"
                    />
                    <component :is="resolveReferenceComponent(detail)" :referenceId="detail.id" />
                  </li>
                </ul>
              </div>
            </div>
          </expand-transition>
        </div>
      </sb-panel>
    </expand-transition>
  </div>
</template>

<script>
import _sumBy from 'lodash/sumBy'
import { OrdersEnum } from '@/enums/modules/routes'
import ProductIdReference from '@/components/notifications/links/ProductId'
import PaymentFailedReference from '@/components/notifications/links/PaymentFailed'

const NOTIFICATIONS_PRIORITY = [2, 10, 5]
const getNotificationPriority = (id) => {
  let p = NOTIFICATIONS_PRIORITY.indexOf(id)
  return p < 0 ? NOTIFICATIONS_PRIORITY.length + 1 : p
}
const NOTIFICATION_NAME_ENUM = {
  10: 'onHoldNotifications.reasons.packagingPreference',
  2: 'onHoldNotifications.reasons.creditCardDeclined',
  5: 'onHoldNotifications.reasons.missingTariffInformation'
}

const NOTIFICATION_DETAIL_COMPONENT_MAP = {
  Product: ProductIdReference,
  Billing: PaymentFailedReference
}

export default {
  name: 'OnHoldOrdersNotificationsPanel',
  components: {},
  mixins: [],
  props: {},
  data: () => ({
    OrdersEnum,
    showNotificationDetails: false
  }),
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {
    this.$shipBobStore.dispatch('appNotifications/fetchOnHoldOrderNotifications')
  },
  beforeDestroy () {},
  destroyed () {},
  beforeUpdate () {},
  updated () {},
  watch: {},
  computed: {
    onHoldOrderNotifications () {
      return this.$shipBobStore.getters['appNotifications/onHoldNotifications']
    },
    notifications () {
      return this.onHoldOrderNotifications.notifications
    },
    filteredNotifications () {
      const supportedNotifications = Object.keys(NOTIFICATION_NAME_ENUM)
      return this.notifications.filter(n => supportedNotifications.includes(`${n.id}`))
    },
    prioritizedNotifications () {
      return this.filteredNotifications.sort((a, b) => getNotificationPriority(a.id) - getNotificationPriority(b.id))
    },
    totalOrdersAffected () {
      return _sumBy(this.filteredNotifications, o => o.totalOrdersAffected)
    },
    hasOnHoldOrderNotifications () {
      return this.filteredNotifications.length > 0
    },
    resolvePaddingClass () {
      return `on-hold-orders-notifications__panel--${this.$route.name}`
    }
  },
  methods: {
    handleShowNotificationDetails () {
      this.showNotificationDetails = !this.showNotificationDetails
    },
    resolveNotificationName (notification) {
      return this.resolveI18nText(NOTIFICATION_NAME_ENUM[notification.id], notification.name)
    },
    resolveReasonText (detail) {
      let key = 'failedNotice'
      if (!Object.keys(NOTIFICATION_DETAIL_COMPONENT_MAP).includes(detail.reference)) key = 'failedNoticeFallback'
      return this.$t(`onHoldNotifications.${key}`, { totalOrdersAffected: detail.totalOrdersAffected })
    },
    resolveReferenceComponent (reason) {
      return NOTIFICATION_DETAIL_COMPONENT_MAP[reason.reference]
    },
    resolveI18nText (key, defaultText) {
      const text = this.$t(key)
      return key === text ? defaultText : text
    }
  },
  directives: {},
  filters: {},
  i18n: {
    messages: {
      'en-US': {
        onHoldNotifications: {
          noticeA: 'You currently have orders ',
          noticeB: ' for the following reasons. Please review and take the required action so that we can fulfill your orders. Learn more about how to resolve these ',
          noticeHelp: 'here.',
          expandText: 'View all',
          failedNotice: '<p class="on-hold-orders-notifications__text on-hold-orders-notifications__text--underlined">{totalOrdersAffected}</p> order(s) on hold ',
          failedNoticeFallback: '<p class="on-hold-orders-notifications__text on-hold-orders-notifications__text--underlined">{totalOrdersAffected}</p> order(s) on hold',
          reasons: {
            packagingPreference: '<p class="on-hold-orders-notifications__text on-hold-orders-notifications__text--bold">Missing packaging preference:</p> All products must have packaging preferences set before we can fulfill orders.',
            creditCardDeclined: '<p class="on-hold-orders-notifications__text on-hold-orders-notifications__text--bold">Payment Failed:</p> Unable to process payment for your outstanding balance.',
            missingTariffInformation: '<p class="on-hold-orders-notifications__text on-hold-orders-notifications__text--bold">Missing customs information:</p> International orders must have customs information for all products.',
            alert: 'You currently have: '
          }
        }
      }
    }
  }
}
</script>

<style lang='scss'>
@import '../../../../assets/scss/shipbob-custom/_mixins.scss';
@import '../../../../node_modules/shipbob-design-system/dist/system/tokens/tokens';
.on-hold-orders-notifications {
  &__panel {
    // didnt want to mess with the css in the other components, they are old and fragile
    margin: 1rem 35px 2rem 35px;
    &--DashboardV2 {
      margin: 10px 1.875rem 0 35px;
    }
  }
  &__wrapper {
    margin-bottom: 0.2rem;
  }
  &__text {
    color: $color-error;
    display: inline;
    &--underlined {
      text-decoration: underline;
    }
    &--bold {
      font-weight: 600;
    }
  }
  &__notifications-container {
    margin-top: 1rem;
  }
  &__link {
    @include link;
    position: relative;
  }
  &__list {
    padding-left: 1.05rem;
  }
  &__list-item {
    color: $color-error;
  }
  &__expand-icon {
    transform: rotate(-90deg);
    transition: all 250ms ease;
    position: absolute;
    right: -1.25rem;
    top: -0.05rem;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    &--expanded {
      top: 0;
      transform: rotate(90deg);
    }
  }
}
</style>
